/**
 * PitchPrint Cart Edit API Discovery & Debug Tool
 * File: assets/pitchprint-cart-edit-debug.js
 * Purpose: Discover and document how PitchPrint's native cart edit functionality works
 * Status: Development/Discovery Tool - Remove or disable in production
 */

(() => {
  'use strict';

  // ⚠️ DISABLED: This debug script wraps PitchPrint methods and may interfere
  // Re-enable only when actively debugging
  console.log('[PP-Edit-Debug] 🚫 Debug script disabled to prevent interference with PitchPrint');
  return;

  const DEBUG_PREFIX = '[PP-Edit-Debug]';
  const discoveries = {
    apiMethods: [],
    eventListeners: [],
    dataAttributes: [],
    clickHandlers: [],
    apiCalls: []
  };

  console.log(`${DEBUG_PREFIX} 🔍 PitchPrint Cart Edit Discovery Tool Loaded`);
  console.log(`${DEBUG_PREFIX} Monitoring for PitchPrint cart edit interactions...`);

  // =====================================
  // DISCOVERY 1: WRAP PPCLIENT API METHODS
  // =====================================

  const wrapPPClientMethods = () => {
    if (!window.ppclient) {
      console.log(`${DEBUG_PREFIX} ⏳ ppclient not yet available, will retry...`);
      setTimeout(wrapPPClientMethods, 500);
      return;
    }

    console.log(`${DEBUG_PREFIX} ✅ ppclient found! Wrapping methods...`);
    console.log(`${DEBUG_PREFIX} ppclient object:`, window.ppclient);

    // Get all methods on ppclient
    const methods = [];
    for (let key in window.ppclient) {
      if (typeof window.ppclient[key] === 'function') {
        methods.push(key);
      }
    }

    console.log(`${DEBUG_PREFIX} 📋 Available ppclient methods:`, methods);
    discoveries.apiMethods = methods;

    // Wrap common cart-related methods
    const methodsToWrap = [
      'show',
      'showApp',
      'editProject',
      'loadProject',
      'fire',
      'hide',
      'on',
      'off',
      'emit'
    ];

    methodsToWrap.forEach(methodName => {
      if (typeof window.ppclient[methodName] === 'function') {
        const original = window.ppclient[methodName];

        window.ppclient[methodName] = function(...args) {
          console.group(`${DEBUG_PREFIX} 🎯 ppclient.${methodName}() CALLED`);
          console.log('Arguments:', args);
          console.log('ppclient._vars state:', window.ppclient._vars ? {...window.ppclient._vars} : 'N/A');
          console.log('Call stack:', new Error().stack);
          console.groupEnd();

          // Record the call
          discoveries.apiCalls.push({
            method: methodName,
            args: args,
            timestamp: Date.now(),
            varsState: window.ppclient._vars ? {...window.ppclient._vars} : null
          });

          return original.apply(this, args);
        };

        console.log(`${DEBUG_PREFIX} ✅ Wrapped ppclient.${methodName}()`);
      }
    });

    // Log ppclient._vars if available
    if (window.ppclient._vars) {
      console.log(`${DEBUG_PREFIX} 📊 Current ppclient._vars:`, {...window.ppclient._vars});
    }
  };

  // =====================================
  // DISCOVERY 2: MONITOR DOM FOR PITCHPRINT CART ELEMENTS
  // =====================================

  const monitorCartEditElements = () => {
    // Selectors that might be PitchPrint cart edit buttons
    const possibleSelectors = [
      '[data-pp-design]',
      '[data-pitchprint]',
      '[data-pp-project]',
      '.pp-cart-edit',
      '.pitchprint-edit',
      '[onclick*="pitchprint"]',
      '[onclick*="ppclient"]',
      'a[href*="pitchprint"]',
      'button[onclick*="pp"]',
      '[data-pp-edit]'
    ];

    possibleSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        console.log(`${DEBUG_PREFIX} 🎯 Found ${elements.length} elements matching: ${selector}`);

        elements.forEach((el, index) => {
          console.group(`${DEBUG_PREFIX} Element ${index + 1}:`);
          console.log('Tag:', el.tagName);
          console.log('Classes:', el.className);
          console.log('Dataset:', {...el.dataset});
          console.log('onclick:', el.getAttribute('onclick'));
          console.log('href:', el.getAttribute('href'));
          console.log('Element:', el);
          console.groupEnd();

          // Record discovery
          discoveries.dataAttributes.push({
            selector,
            element: el.tagName,
            dataset: {...el.dataset},
            onclick: el.getAttribute('onclick'),
            href: el.getAttribute('href')
          });

          // Add click listener to intercept
          if (!el.dataset.debugListenerAdded) {
            el.dataset.debugListenerAdded = 'true';

            el.addEventListener('click', function(e) {
              console.group(`${DEBUG_PREFIX} 🖱️ PITCHPRINT EDIT CLICKED`);
              console.log('Element:', this);
              console.log('Dataset:', {...this.dataset});
              console.log('Event:', e);
              console.log('ppclient state before click:', window.ppclient?._vars);
              console.groupEnd();

              // Record click
              discoveries.clickHandlers.push({
                element: this.tagName,
                dataset: {...this.dataset},
                timestamp: Date.now()
              });

              // Log state after click (delayed)
              setTimeout(() => {
                console.log(`${DEBUG_PREFIX} 📊 ppclient state AFTER click:`, window.ppclient?._vars);
              }, 100);
            }, true); // Capture phase to catch before PitchPrint
          }
        });
      }
    });
  };

  // =====================================
  // DISCOVERY 3: MONITOR PPCLIENT EVENT SYSTEM
  // =====================================

  const monitorPPClientEvents = () => {
    if (!window.ppclient || typeof window.ppclient.on !== 'function') {
      setTimeout(monitorPPClientEvents, 500);
      return;
    }

    const eventNames = [
      'project-loaded',
      'project-saved',
      'project-cleared',
      'designer-opened',
      'designer-closed',
      'edit-started',
      'edit-completed'
    ];

    eventNames.forEach(eventName => {
      try {
        window.ppclient.on(eventName, (data) => {
          console.log(`${DEBUG_PREFIX} 📡 Event: ${eventName}`, data);
          discoveries.eventListeners.push({
            event: eventName,
            data,
            timestamp: Date.now()
          });
        });
      } catch (e) {
        // Event might not exist, that's OK
      }
    });

    console.log(`${DEBUG_PREFIX} 📡 Monitoring PitchPrint events:`, eventNames);
  };

  // =====================================
  // DISCOVERY 4: ANALYZE NETWORK REQUESTS
  // =====================================

  const monitorNetworkForPitchPrint = () => {
    // Hook into fetch
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      if (url && (url.includes('pitchprint') || url.includes('pprint'))) {
        console.log(`${DEBUG_PREFIX} 🌐 Fetch to PitchPrint:`, url, args);
      }
      return originalFetch.apply(this, args);
    };

    // Hook into XMLHttpRequest
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      if (url && (url.includes('pitchprint') || url.includes('pprint'))) {
        console.log(`${DEBUG_PREFIX} 🌐 XHR to PitchPrint:`, method, url);
      }
      return originalOpen.apply(this, arguments);
    };

    console.log(`${DEBUG_PREFIX} 🌐 Network monitoring active`);
  };

  // =====================================
  // DISCOVERY 5: SCAN FOR CART LINE ITEMS WITH PITCHPRINT DATA
  // =====================================

  const scanCartForPitchPrintData = () => {
    console.group(`${DEBUG_PREFIX} 🛒 Scanning cart for PitchPrint data...`);

    // Find all cart line items
    const cartItemSelectors = [
      '.cart-item',
      '.mini-cart__navigation li',
      'cart-items tr',
      '[data-variant-id]'
    ];

    const foundItems = [];
    cartItemSelectors.forEach(selector => {
      const items = document.querySelectorAll(selector);
      if (items.length > 0) {
        console.log(`Found ${items.length} items with selector: ${selector}`);
        items.forEach((item, index) => {
          // Look for PitchPrint project IDs
          const textContent = item.textContent || '';
          const innerHTML = item.innerHTML || '';

          // Check for project ID patterns
          const projectIdMatch = textContent.match(/([a-f0-9]{32,40})/);
          if (projectIdMatch) {
            console.log(`Item ${index + 1} - Possible project ID:`, projectIdMatch[1]);
            console.log('Dataset:', {...item.dataset});
            console.log('HTML snippet:', innerHTML.substring(0, 200));

            foundItems.push({
              index: index + 1,
              possibleProjectId: projectIdMatch[1],
              dataset: {...item.dataset}
            });
          }
        });
      }
    });

    console.log('Cart items with potential PitchPrint data:', foundItems);
    console.groupEnd();
  };

  // =====================================
  // HELPER: GENERATE DISCOVERY REPORT
  // =====================================

  window.generatePitchPrintDiscoveryReport = () => {
    console.clear();
    console.log('='.repeat(80));
    console.log('PITCHPRINT CART EDIT API DISCOVERY REPORT');
    console.log('='.repeat(80));
    console.log('');

    console.log('1. PPCLIENT METHODS FOUND:');
    console.log(discoveries.apiMethods);
    console.log('');

    console.log('2. API CALLS RECORDED:');
    console.table(discoveries.apiCalls);
    console.log('');

    console.log('3. DATA ATTRIBUTES DISCOVERED:');
    console.table(discoveries.dataAttributes);
    console.log('');

    console.log('4. CLICK HANDLERS TRIGGERED:');
    console.table(discoveries.clickHandlers);
    console.log('');

    console.log('5. EVENT LISTENERS:');
    console.table(discoveries.eventListeners);
    console.log('');

    console.log('='.repeat(80));
    console.log('RECOMMENDED IMPLEMENTATION:');
    console.log('='.repeat(80));

    if (discoveries.apiCalls.length > 0) {
      const firstCall = discoveries.apiCalls[0];
      console.log(`✅ Use: window.ppclient.${firstCall.method}(${JSON.stringify(firstCall.args)})`);
    } else {
      console.log('⚠️ No API calls captured yet. Try clicking a PitchPrint edit button.');
    }

    console.log('');
    console.log('To copy this report:');
    console.log('copy(window.generatePitchPrintDiscoveryReport.reportData)');
    console.log('');

    return discoveries;
  };

  // Expose discoveries for easy access
  window.generatePitchPrintDiscoveryReport.reportData = discoveries;

  // =====================================
  // INITIALIZATION
  // =====================================

  const init = () => {
    console.log(`${DEBUG_PREFIX} 🚀 Initializing discovery tools...`);

    wrapPPClientMethods();
    monitorPPClientEvents();
    monitorNetworkForPitchPrint();

    // Run DOM scans periodically
    scanCartForPitchPrintData();
    monitorCartEditElements();

    // Re-scan when DOM changes
    const observer = new MutationObserver(() => {
      monitorCartEditElements();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Periodic scans
    setInterval(() => {
      monitorCartEditElements();
    }, 2000);

    console.log(`${DEBUG_PREFIX} ✅ Discovery tools active!`);
    console.log(`${DEBUG_PREFIX} 📝 Run generatePitchPrintDiscoveryReport() after clicking edit`);
  };

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Add visual indicator
  const indicator = document.createElement('div');
  indicator.style.cssText = `
    position: fixed;
    top: 10px;
    right: 10px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    z-index: 99999;
    font-family: monospace;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    cursor: pointer;
  `;
  indicator.textContent = '🔍 PP Debug Active';
  indicator.title = 'Click to generate discovery report';
  indicator.onclick = () => {
    window.generatePitchPrintDiscoveryReport();
  };

  document.body.appendChild(indicator);

  // Auto-remove after 10 seconds
  setTimeout(() => {
    indicator.style.transition = 'opacity 0.5s';
    indicator.style.opacity = '0.3';
  }, 10000);

  console.log(`${DEBUG_PREFIX} 🎯 Debug indicator added to top-right corner`);
})();
